/**
 * localStorage-backed offline write queue for autosave (#160).
 *
 * #145 shipped a local DRAFT safety net (itemDraft.js) — unsaved edits mirrored
 * to localStorage purely so a crash/closed-tab doesn't lose them, offered back
 * for restore, never sent anywhere on their own.
 *
 * This is the other half: when an autosave attempt (useAutosave.js) genuinely
 * cannot reach the server — offline, DNS/network failure, request timeout —
 * the edit that WOULD have been sent is persisted here instead of being
 * silently dropped. It survives a reload and is retried when connectivity
 * returns (the `online` browser event in useAutosave.js).
 *
 * Keyed per item id, one entry each: a second offline edit to the same item
 * overwrites the first — only the LATEST edit needs to reach the server, no
 * need to replay every intermediate keystroke-settle.
 *
 * The stored `payload` is an opaque blob as far as this module is concerned
 * (useAutosave.js decides its shape); this module only owns storage.
 */

const QUEUE_KEY = 'rmdv_autosave_offline_queue';

const isStorageAvailable = () => {
  try {
    const t = '__rmdv_autosave_queue_test__';
    localStorage.setItem(t, t);
    localStorage.removeItem(t);
    return true;
  } catch {
    return false;
  }
};

function readQueue() {
  if (!isStorageAvailable()) return {};
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeQueue(queue) {
  if (!isStorageAvailable()) return;
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Best-effort only (e.g. quota exceeded) — never block editing on this.
  }
}

/** Queue (or replace) the pending save for `itemId`. */
export function enqueueSave(itemId, payload) {
  if (!itemId) return;
  const queue = readQueue();
  queue[itemId] = { payload, queuedAt: Date.now() };
  writeQueue(queue);
}

/** The queued entry `{ payload, queuedAt }` for `itemId`, or null. */
export function getQueuedSave(itemId) {
  if (!itemId) return null;
  const queue = readQueue();
  return queue[itemId] || null;
}

/** Remove the queued entry for `itemId` (call after a successful flush, or a
 * 409 conflict — the queued write no longer applies cleanly and the normal
 * conflict-resolution UI takes over instead of a silent blind retry). */
export function dequeueSave(itemId) {
  if (!itemId) return;
  const queue = readQueue();
  if (itemId in queue) {
    delete queue[itemId];
    writeQueue(queue);
  }
}

/** All queued entries as `[{ itemId, payload, queuedAt }]`, oldest first — so
 * a reconnect flush retries writes in the order they were made. */
export function getAllQueued() {
  const queue = readQueue();
  return Object.entries(queue)
    .map(([itemId, entry]) => ({ itemId, ...entry }))
    .sort((a, b) => a.queuedAt - b.queuedAt);
}

/** True if anything is queued (any item, not just the one currently open). */
export function hasQueuedSaves() {
  return getAllQueued().length > 0;
}

/** Clear the whole queue. Used by tests and an explicit "discard offline edits". */
export function clearQueue() {
  writeQueue({});
}
